import { useState } from 'react';
import { Link } from 'react-router-dom';
import { comics, categories, faqs, hotKeywords } from '@/mocks/comics';
import { OrganizationStructuredData, WebSiteStructuredData, FAQPageStructuredData } from '@/components/feature/SEOComponents';
import PageHead from '@/components/base/PageHead';

function HeroSection() {
  const [searchValue, setSearchValue] = useState('');

  return (
    <section className="relative w-full overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=A%20beautiful%20Korean%20manhwa%20style%20digital%20art%20wallpaper%20with%20soft%20warm%20gradient%20background%2C%20subtle%20floating%20comic%20panel%20elements%2C%20delicate%20cherry%20blossom%20petals%2C%20artistic%20watercolor%20texture%2C%20warm%20peach%20and%20cream%20color%20palette%2C%20dreamy%20atmospheric%20lighting%2C%20clean%20composition%2C%20no%20text&width=1600&height=800&seq=hero-bg-home&orientation=landscape"
          alt="韩国漫画网 - 免费韩漫在线看平台"
          title="韩国漫画网 韩国漫画网站大全"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-background-50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-6 py-20 md:py-28">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
            韩国漫画网 <span className="text-accent-400">韩国漫画网站大全</span>
          </h1>
          <p className="text-white/80 text-base md:text-lg mb-8 max-w-xl mx-auto leading-relaxed">
            海量<strong>免费韩漫在线看</strong>，支持下拉式阅读，实时追更热门漫画，日更不断更。
            汇聚全部分类的<strong>韩国漫画网站大全</strong>资源。
          </p>

          {/* Search */}
          <div className="relative max-w-lg mx-auto">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="搜索漫画名称、作者..."
              className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/95 backdrop-blur-sm border-0 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 transition-all"
            />
          </div>

          {/* Hot Keywords */}
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {hotKeywords.slice(0, 6).map((kw) => (
              <span
                key={kw}
                className="px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-white/90 text-xs cursor-pointer hover:bg-white/25 transition-colors"
              >
                {kw}
              </span>
            ))}
          </div>

          {/* Stats */}
          <div className="flex items-center justify-center gap-6 md:gap-10 mt-10">
            {[
              { value: '2000+', label: '漫画资源' },
              { value: '12+', label: '分类频道' },
              { value: '500万+', label: '月活读者' },
              { value: '日更', label: '更新频率' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-xl md:text-2xl font-bold text-accent-400 font-heading">{stat.value}</div>
                <div className="text-xs text-white/60 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function HotComicsSection() {
  const hotComics = comics.filter((c) => c.isHot).slice(0, 8);

  return (
    <section className="px-4 md:px-6 py-12 md:py-16">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              <strong>热门韩漫</strong>
            </h2>
            <p className="text-foreground-500 text-sm mt-1">最受读者欢迎的韩国漫画推荐</p>
          </div>
          <Link
            to="/category/all"
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            查看全部 <i className="ri-arrow-right-line text-xs"></i>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
          {hotComics.map((comic) => (
            <Link
              key={comic.id}
              to={`/comic/${comic.id}`}
              className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
            >
              <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
                <img
                  src={comic.cover}
                  alt={`${comic.title} 韩国漫画 免费韩漫在线看`}
                  title={`${comic.title} - 热门韩漫`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-2 left-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    comic.status === '连载中' ? 'bg-primary-500 text-white' : 'bg-secondary-500 text-white'
                  }`}>
                    {comic.status}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-400 text-xs"></i>
                    <span className="text-white text-xs font-medium">{comic.rating}</span>
                    <span className="text-white/60 text-xs ml-2">
                      {(comic.views / 10000).toFixed(1)}万人气
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-3">
                <h3 className="font-heading text-sm font-semibold text-foreground-950 truncate group-hover:text-primary-600 transition-colors">
                  {comic.title}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryQuickLinks() {
  return (
    <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            <strong>韩国漫画网站大全</strong> 分类浏览
          </h2>
          <p className="text-foreground-500 text-sm mt-2">按分类找到你想看的韩漫，覆盖全部分类频道</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
          {categories.filter((c) => c.slug !== 'all').map((cat, idx) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-xl bg-background-50 hover:bg-accent-50 transition-all duration-300 cursor-pointer"
            >
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-accent-100 group-hover:bg-accent-200 transition-colors">
                <i className={`${cat.icon} text-2xl md:text-3xl text-accent-600`}></i>
              </div>
              <span className="font-heading text-sm md:text-base font-semibold text-foreground-800 group-hover:text-accent-700 transition-colors">
                {cat.name}
              </span>
              <span className="text-xs text-foreground-400">
                {comics.filter((c) => c.categorySlug === cat.slug).length} 部作品
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestUpdatesSection() {
  const latestComics = [...comics]
    .sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime())
    .slice(0, 6);

  return (
    <section className="px-4 md:px-6 py-12 md:py-16">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              <strong>韩漫追更</strong> 最新更新
            </h2>
            <p className="text-foreground-500 text-sm mt-1">日更不断更，第一时间同步最新章节</p>
          </div>
          <Link
            to="/category/all"
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            更多更新 <i className="ri-arrow-right-line text-xs"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {latestComics.map((comic) => (
            <Link
              key={comic.id}
              to={`/comic/${comic.id}`}
              className="flex gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200/70 transition-all duration-300 cursor-pointer group"
            >
              <div className="w-20 h-28 md:w-24 md:h-32 flex-shrink-0 rounded-lg overflow-hidden">
                <img
                  src={comic.cover}
                  alt={`${comic.title} 韩漫追更`}
                  title={`${comic.title} - 最新更新`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex-1 min-w-0 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 truncate group-hover:text-primary-600 transition-colors">
                      {comic.title}
                    </h3>
                    {comic.isNew && (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-accent-500 text-white whitespace-nowrap">
                        NEW
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-foreground-500 line-clamp-2">
                    {comic.shortDescription}
                  </p>
                </div>
                <div className="flex items-center gap-3 text-xs text-foreground-400 mt-2">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> {comic.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> {comic.updateTime}
                  </span>
                  <span className="text-primary-500 font-medium whitespace-nowrap">
                    更新至{comic.chapters.length}话
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureSection() {
  return (
    <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            为什么选择 <strong>韩国漫画网</strong>
          </h2>
          <p className="text-foreground-500 text-sm mt-2">为韩漫爱好者打造的最佳在线阅读平台</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              icon: 'ri-book-open-line',
              title: '海量免费资源',
              desc: '汇聚韩国漫画网站大全，上千部热门漫画免费在线阅读，覆盖恋爱、动作、奇幻所有分类。',
            },
            {
              icon: 'ri-file-list-line',
              title: '下拉式阅读',
              desc: '专为移动端优化的下拉式漫画阅读体验，无需翻页，流畅丝滑的纵向滚动，沉浸式享受。',
            },
            {
              icon: 'ri-timer-flash-line',
              title: '实时追更',
              desc: '日更不断更，第一时间同步更新热门漫画最新章节，让您不再错过任何精彩剧情发展。',
            },
            {
              icon: 'ri-device-line',
              title: '多端适配',
              desc: '全面支持PC端和手机浏览，自适应屏幕尺寸，随时随地畅享韩漫在线阅读的乐趣。',
            },
          ].map((feature) => (
            <div key={feature.title} className="p-6 rounded-xl bg-background-50 text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 mx-auto mb-4">
                <i className={`${feature.icon} text-xl text-accent-600`}></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">{feature.title}</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="px-4 md:px-6 py-12 md:py-16">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            <strong>免费韩漫在线看</strong> 常见问题
          </h2>
          <p className="text-foreground-500 text-sm mt-2">关于韩国漫画网的常见疑问解答</p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <div key={idx} className="rounded-xl bg-background-100 overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer hover:bg-background-200/50 transition-colors"
              >
                <h3 className="font-heading text-sm md:text-base font-medium text-foreground-900 pr-4">
                  {faq.question}
                </h3>
                <i
                  className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 text-lg flex-shrink-0 ${
                    openIndex === idx ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {openIndex === idx && (
                <div className="px-4 md:px-5 pb-4 md:pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="px-4 md:px-6 py-16 md:py-20 bg-accent-500">
      <div className="max-w-2xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
          开始你的<strong>韩漫在线阅读</strong>之旅
        </h2>
        <p className="text-white/80 text-sm md:text-base mb-8">
          立即加入韩国漫画网，海量免费韩漫等你来发现
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/category/all"
            className="px-8 py-3 rounded-full bg-white text-accent-600 font-semibold text-sm hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            立即浏览漫画
          </Link>
          <Link
            to="/about"
            className="px-8 py-3 rounded-full border-2 border-white/40 text-white font-semibold text-sm hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
          >
            了解更多
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <PageHead
        title="韩国漫画网 - 韩国漫画网站大全 | 免费韩漫在线看平台入口"
        description="韩国漫画网提供韩国漫画网站大全资源，海量免费韩漫在线看，支持下拉式阅读，实时追更热门漫画，日更不断更。"
        keywords="韩国漫画网, 韩国漫画网站, 韩国漫画网站大全, 免费韩漫, 韩漫在线看, 下拉式漫画, 韩漫追更, 漫画在线阅读, 热门韩漫, 韩漫大全"
        canonicalUrl="https://www.koreancomic.cn/"
      />
      <OrganizationStructuredData />
      <WebSiteStructuredData />
      <FAQPageStructuredData faqs={faqs.slice(0, 5)} />

      <main>
        <HeroSection />
        <HotComicsSection />
        <CategoryQuickLinks />
        <LatestUpdatesSection />
        <FeatureSection />
        <FAQSection />
        <CTASection />
      </main>
    </>
  );
}